import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { orderLayersByConfiguration, useGetVersionConfigQuery } from "fond/api";
import { useGetOverlaysQuery } from "fond/api/overlaysSlice";
import { MAX_OVERLAY_COUNT } from "fond/constants";
import { Configuration, GroupConfig, MultiProject, Project } from "fond/types";
import { CustomOverlayItem } from "fond/types/overlays";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { isMultiProject } from "fond/utils/project";

const EMPTY_OVERLAY_METADATA: Record<string, OverlayMetadata> = {};

/**
 * Combined data structure for an overlay with its configuration
 */
export interface OverlayWithConfig {
  overlay: CustomOverlayItem;
  config: Configuration;
}

/**
 * Metadata for a single overlay, keyed by overlay group ID
 */
export interface OverlayMetadata {
  /** The overlay's unique ID */
  overlayId: string;
  /** The source version ID for this overlay */
  sourceVersionId: string;
  /** The source project ID for this overlay */
  sourceProjectId: string;
}

/**
 * Result type for the useOverlaysWithConfigs hook
 */
export interface UseOverlaysWithConfigsResult {
  overlays: Configuration | null;
  /** Flat array of overlay layer configs, extracted from the merged overlay configuration */
  overlayLayerConfigs: Array<LayerConfig | SublayerConfig>;
  /** Map of overlay group ID to overlay metadata */
  overlayMetadata: Record<string, OverlayMetadata>;
  isLoading: boolean;
  error: unknown;
}

/**
 * Merges multiple overlay configurations into a single Configuration.
 * Each overlay is wrapped in a GROUP node.
 * Returns the merged configuration and metadata for each overlay.
 */
const mergeOverlayConfigs = (
  overlaysWithConfigs: OverlayWithConfig[]
): {
  config: Configuration;
  overlayMetadata: Record<string, OverlayMetadata>;
} | null => {
  if (overlaysWithConfigs.length === 0) {
    return null;
  }

  const mergedEntities: Configuration["Data"]["entities"] = {};
  const mergedIds: string[] = [];
  const overlayGroupIds: string[] = [];
  const overlayMetadata: Record<string, OverlayMetadata> = {};

  overlaysWithConfigs.forEach(({ overlay, config }, index) => {
    const overlayGroupId = `overlay-group-${overlay.ID}`;

    // Store the metadata for this overlay
    overlayMetadata[overlayGroupId] = {
      overlayId: overlay.ID,
      sourceVersionId: overlay.SourceVersion.ID,
      sourceProjectId: overlay.SourceVersion.Project.ID,
    };

    // Copy all entities from this overlay's config
    Object.entries(config.Data.entities).forEach(([id, entity]) => {
      if (entity) {
        // Update ParentID for children that should be nested under this overlay's GROUP
        if (config.MapChildren.includes(id)) {
          mergedEntities[id] = {
            ...entity,
            ParentID: overlayGroupId,
          } as typeof entity;
        } else {
          mergedEntities[id] = entity;
        }
        mergedIds.push(id);
      }
    });

    // Create a GROUP wrapper for this overlay
    // All overlay groups will be children of the parent "Overlays" group
    const overlayGroup: GroupConfig = {
      ID: overlayGroupId,
      Type: "GROUP",
      Key: `overlay-${overlay.ID}`,
      Label: overlay.Name,
      IsVisible: true,
      Hidden: false,
      Position: index,
      GlobalPosition: index,
      ParentID: "overlays-parent-group",
      RootID: null,
      Children: config.MapChildren,
    };

    mergedEntities[overlayGroupId] = overlayGroup;
    mergedIds.push(overlayGroupId);
    overlayGroupIds.push(overlayGroupId);
  });

  // Create the parent "Overlays" GROUP that contains all overlay groups
  const parentOverlaysGroup: GroupConfig = {
    ID: "overlays-parent-group",
    Type: "GROUP",
    Key: "overlays-parent",
    Label: "Overlays",
    IsVisible: true,
    Hidden: false,
    Position: 0,
    GlobalPosition: 0,
    ParentID: null,
    RootID: null,
    Children: overlayGroupIds,
  };

  mergedEntities["overlays-parent-group"] = parentOverlaysGroup;
  mergedIds.push("overlays-parent-group");

  // Create the merged configuration
  return {
    config: {
      ID: "overlays-merged-root",
      Key: "overlays-merged",
      SourceID: "overlays",
      Type: "MapLayerConfig",
      MapChildren: ["overlays-parent-group"],
      Data: {
        ids: mergedIds,
        entities: mergedEntities,
      },
    },
    overlayMetadata,
  };
};

/**
 * Merges a project configuration with an overlay configuration into a single unified Configuration.
 * This is useful when consumers need to treat project layers and overlay layers as one combined set
 * (e.g. for feature selection popups that need to identify features from both sources).
 *
 * @param config - The base project configuration
 * @param overlayConfig - The merged overlay configuration (from useOverlaysWithConfigs), or null if no overlays
 * @returns A single Configuration containing both project and overlay entities
 */
export const mergeConfigWithOverlays = (config: Configuration, overlayConfig: Configuration | null): Configuration => {
  if (!overlayConfig) return config;
  return {
    ...config,
    Data: {
      ids: [...config.Data.ids, ...overlayConfig.Data.ids],
      entities: { ...config.Data.entities, ...overlayConfig.Data.entities },
    },
    MapChildren: [...config.MapChildren, ...overlayConfig.MapChildren],
  };
};

/**
 * Hook that loads overlays for a project/version and their associated root configurations.
 *
 * This hook:
 * 1. Fetches the list of custom overlays for the given version or multi-project
 * 2. For each overlay, fetches the root configuration of its source version
 * 3. Waits for ALL configurations to load before returning data (Promise.all behavior)
 * 4. Merges all overlay configs into a single Configuration with a GROUP for each overlay
 * 5. Returns null while loading, merged Configuration once all data is ready
 *
 * @param versionId - The version ID to load overlays for
 * @param project - The current project (used to determine if multi-project)
 * @returns Merged configuration containing all overlays. `overlays` is null until all configs load.
 */
export const useOverlaysWithConfigs = (versionId: string | undefined, project: Project | MultiProject | undefined): UseOverlaysWithConfigsResult => {
  const multiProjectId = project && isMultiProject(project) ? project.ID : undefined;
  const {
    data: overlaysData,
    isLoading: isLoadingOverlays,
    error: overlaysError,
  } = useGetOverlaysQuery(multiProjectId ? { multiProjectId } : versionId ? { versionId } : skipToken, {
    skip: !versionId && !multiProjectId,
  });

  // Extract the overlay items and memoize to avoid recreating on every render
  const overlayItems = useMemo(() => overlaysData?.CustomOverlays ?? [], [overlaysData]);

  // Load root configuration for each overlay's source version
  // IMPORTANT: We use a fixed-size array to satisfy React's rules of hooks.
  // If we mapped directly over overlayItems, adding/removing overlays would change
  // the number of hook calls between renders, causing React to error.
  // Queries beyond the actual overlay count are skipped via the skip parameter (no network cost).
  const configQueries = Array.from({ length: MAX_OVERLAY_COUNT }, (_, index) => {
    const overlay = overlayItems[index];
    const sourceVersionId = overlay?.SourceVersion?.ID;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useGetVersionConfigQuery(sourceVersionId ?? skipToken, {
      skip: !sourceVersionId,
    });
  });

  // Aggregate loading and error states across all queries
  const activeQueries = configQueries.slice(0, overlayItems.length);
  const isLoadingConfigs = activeQueries.some((query) => query.isLoading);
  const configErrors = activeQueries.map((query) => query.error).filter(Boolean);
  const allConfigsLoaded = activeQueries.length > 0 && activeQueries.every((query) => query.data !== undefined || query.error);

  // Overall loading state - wait for overlays AND all configs
  const isLoading = isLoadingOverlays || isLoadingConfigs;

  // Extract stable references to config data for useMemo dependencies.
  // configQueries is a new array each render, but RTK Query's .data references are stable.
  const configDatas = configQueries.map((query) => query.data);

  // Build array of overlay configs, then merge into single Configuration
  const overlaysWithConfigs: OverlayWithConfig[] = useMemo(() => {
    if (isLoading || !allConfigsLoaded) {
      return [];
    }

    return overlayItems
      .map((overlay, index) => {
        const config = configDatas[index];
        // Filter out overlays where config failed to load
        if (!config) {
          return null;
        }
        // Remove projectComments from MapChildren
        return {
          overlay,
          config: {
            ...config,
            MapChildren: config.MapChildren.filter((id) => id !== "projectComments"),
          },
        };
      })
      .filter((item): item is OverlayWithConfig => item !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, allConfigsLoaded, overlayItems, ...configDatas]);

  // Merge all overlay configs into a single Configuration
  const mergedResult = useMemo(() => mergeOverlayConfigs(overlaysWithConfigs), [overlaysWithConfigs]);

  const overlayConfig = mergedResult?.config ?? null;

  // Extract a flat array of overlay layer configs for consumers that need to match features
  const overlayLayerConfigs = useMemo(() => (overlayConfig ? orderLayersByConfiguration(overlayConfig) : []), [overlayConfig]);

  return {
    overlays: overlayConfig,
    overlayLayerConfigs,
    overlayMetadata: mergedResult?.overlayMetadata ?? EMPTY_OVERLAY_METADATA,
    isLoading,
    error: overlaysError || (configErrors.length > 0 ? configErrors[0] : undefined),
  };
};
